import PropTypes from 'prop-types';
import React from 'react';

import get from 'lodash.get';
import { SECTION_NAMES } from '../../../common/utils/studentsSortingHelpers';
import { constructBurstAssessmentLink } from '../../../common/navigation/navigation';
import { BURST } from '../../../constants/applications';
import {
    CS_MEASURES,
    MEASURE_NAMES,
    MEASURE_BY_SKILL_NAME,
    MEASURE_SCORE_BY_SKILL_NAME,
    SKILL_NAMES_BY_APPLICATION,
    NECESSITY,
    SKILL_DISPLAY_NAMES,
    MEASURE_SCORE_DISPLAY_NAMES,
    DEC_MEASURES,
} from '../../../constants/assessments';

import AssessLink from '../assess-link/AssessLink';
import { useAppContext } from '../../../common/contexts/AppContext';
import { resultType, burstResultType, studentType } from '../../../constants/prop-types';
import calculateBurstAvailability from '../../../common/utils/calculateBurstAvailability';
import BurstMeasureScore from './burst-measure-score/BurstMeasureScore';
import BMInterventionBanner from '../../banner/BMInterventionBanner';
import { usePopupContext } from '../../../common/contexts/PopupContext';
import { POPUP_NAMES } from '../../../constants/popups';
import { BM_PERIODS, PERIOD } from '../../../constants/enrollment';
import TableHeader from '../table-header/TableHeader';

export const getBurstDetailsForPeriod = (
    skillName,
    student,
    classId,
    period,
    availability,
    burstResults,
    popupContext,
) => {
    const burstStatuses = student.appStatuses.burst[period].status;
    const measure = MEASURE_BY_SKILL_NAME[skillName];
    const status = burstStatuses[measure];
    const isAlt = status.isAlt;
    const measureAvailability = availability[period][measure];
    const measureGenus = CS_MEASURES.includes(measure) ? MEASURE_NAMES.CS : measure;
    const measureResult = burstResults.find(result => result.measureGenus === measureGenus);
    const params = {
        studentId: student.id,
        classeId: classId,
        period,
        measure,
    };
    const mode = [NECESSITY.NOT_PERMITTED, NECESSITY.NOT_ENOUGH_INFO].includes(
        measureAvailability.necessity,
    )
        ? AssessLink.MODE.DISABLED
        : measureAvailability.necessity;
    if (measureResult) {
        return (
            <BurstMeasureScore measure={measure} params={params} measureResult={measureResult} />
        );
    }
    const {
        actions: {
            showOptionalAssessNoticePopup,
            showCSFormSelectionPopup,
            showBurstAltFormSelectionPopup,
        },
    } = popupContext;
    const probeConfig = status.config.find(config => config.is_alt === false);

    if (!probeConfig) {
        return (
            <AssessLink
                mode={
                    mode === AssessLink.MODE.OPTIONAL
                        ? AssessLink.MODE.OPTIONAL_DISABLED
                        : AssessLink.MODE.DISABLED
                }
            />
        );
    }

    const assessmentParams = {
        ...params,
        is_alt: 0,
        probeId: probeConfig.probeId,
    };
    if (CS_MEASURES.includes(measure)) {
        return (
            <AssessLink
                mode={mode}
                onClickHandler={() =>
                    showCSFormSelectionPopup({
                        studentId: student.id,
                        isAlt,
                        formData: status.config,
                        lastAssessedProbeId: status.latestAssessedFormId,
                    })
                }
            />
        );
    }
    if (isAlt) {
        return (
            <AssessLink
                mode={mode}
                onClickHandler={() =>
                    showBurstAltFormSelectionPopup({
                        studentId: student.id,
                        skillName,
                        formData: status.config,
                    })
                }
            />
        );
    }
    if (mode === AssessLink.MODE.OPTIONAL) {
        return (
            <AssessLink
                mode={mode}
                onClickHandler={() =>
                    showOptionalAssessNoticePopup({
                        name: POPUP_NAMES.OPTIONAL_ASSESS,
                        path: constructBurstAssessmentLink(assessmentParams),
                    })
                }
            />
        );
    }
    return <AssessLink mode={mode} path={constructBurstAssessmentLink(assessmentParams)} />;
};

export const isMeasureAvailable = (availability, period, skillName) => {
    const measure = MEASURE_BY_SKILL_NAME[skillName];
    return get(availability, `[${period}][${measure}].available`);
};

export const getNotificationBanner = (student, period, availability) => {
    const needsSupport = Object.values(availability[period])
        .filter(measureAvailability => DEC_MEASURES.includes(measureAvailability.measure))
        .some(measureAvailability => measureAvailability.necessity === NECESSITY.REQUIRED);
    const isBannerVisible = Object.values(availability[period]).some(
        measureAvailability => measureAvailability.necessity === NECESSITY.REQUIRED,
    );
    return (
        isBannerVisible && (
            <BMInterventionBanner studentUid={student.id} needsSupport={needsSupport} />
        )
    );
};

const BurstSummary = props => {
    const { student, dibelsResults, burstResults } = props;
    const {
        bmWindow: { period },
        classId,
    } = useAppContext();
    const popupContext = usePopupContext();
    const gatedMeasureStatuses = BM_PERIODS.reduce((statuses, bmPeriod) => {
        return {
            ...statuses,
            [bmPeriod]: get(student.appStatuses.dibels8[bmPeriod], 'gatedMeasuresStatus', {}),
        };
    }, {});
    const availability = calculateBurstAvailability(
        student.id,
        student.grade,
        gatedMeasureStatuses,
        burstResults,
        dibelsResults,
    );

    // if current period is SUMMER than use EOY instead
    const updatedPeriod = period === PERIOD.SUMMER ? PERIOD.EOY : period;

    return (
        <>
            <TableHeader columns={[SECTION_NAMES.INTERVENTION]} />
            <div className='dashboard-app-sub-header'>
                {getNotificationBanner(student, updatedPeriod, availability)}
                <div className='dashboard-period-info'>
                    {<div className='dashboard-period-label'>{`${updatedPeriod} Form`}</div>}
                </div>
            </div>

            <div className='dashboard-row-measure-scores'>
                {SKILL_NAMES_BY_APPLICATION[BURST].filter(value =>
                    isMeasureAvailable(availability, updatedPeriod, value),
                ).map(skillName => (
                    <div className='dashboard-measure-info' key={skillName}>
                        <>
                            {getBurstDetailsForPeriod(
                                skillName,
                                student,
                                classId,
                                updatedPeriod,
                                availability,
                                get(burstResults, `${updatedPeriod}.${student.id}`, []),
                                popupContext,
                            )}
                        </>
                        <div className='dashboard-skill'>
                            <span className='dashboard-skill-name'>{SKILL_DISPLAY_NAMES[skillName]}</span>
                            <span className='dashboard-measure-name'>
                                {
                                    MEASURE_SCORE_DISPLAY_NAMES[
                                        MEASURE_SCORE_BY_SKILL_NAME[skillName]
                                    ]
                                }
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            <div className='dashboard-empty-divider' />
        </>
    );
};

BurstSummary.propTypes = {
    student: studentType.isRequired,
    dibelsResults: PropTypes.shape({
        BOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        MOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        EOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        PM: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
    }),
    burstResults: PropTypes.shape({
        BOY: PropTypes.objectOf(PropTypes.arrayOf(burstResultType)),
        MOY: PropTypes.objectOf(PropTypes.arrayOf(burstResultType)),
        EOY: PropTypes.objectOf(PropTypes.arrayOf(burstResultType)),
        PM: PropTypes.objectOf(PropTypes.arrayOf(burstResultType)),
    }),
};

BurstSummary.defaultProps = {
    dibelsResults: {},
    burstResults: {},
};

export default BurstSummary;
